import { css, styled } from '@withjoy/joykit';
import { pxToRem } from '@withjoy/joykit/theme';
import { MEGA_NAV_GEOMETRY as G } from './megaNav.geometry';

/*
 * Inert, dependency-light twin of the mega nav's bar (desktop) and strip (mobile). Only
 * `megaNav.geometry.ts` is shared with the real nav: this file must stay out of the
 * flag-off bundle's way, so it imports none of the nav's config or style modules.
 */

export const PlaceholderMobile = styled.nav`
  display: block;
  width: 100%;
  border-bottom: 1px solid ${({ theme }) => theme.colors.mono3};
  background-color: ${({ theme }) => theme.colors.white};
  ${({ theme }) => theme.mediaQueries.up({ breakpointAlias: 'md' })} {
    display: none;
  }
`;

export const PlaceholderDesktop = styled.nav`
  display: none;
  width: 100%;
  border-bottom: 1px solid ${({ theme }) => theme.colors.mono3};
  background-color: ${({ theme }) => theme.colors.white};
  ${({ theme }) => theme.mediaQueries.up({ breakpointAlias: 'md' })} {
    display: block;
  }
`;

export const PlaceholderBar = styled.ul`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: ${({ theme }) => theme.space[G.barGapSpace]};
  margin: 0;
  padding: ${G.barPaddingTopPx}px 0 ${({ theme }) => theme.space[G.barPaddingBottomSpace]};
  list-style: none;
`;

export const PlaceholderStrip = styled.ul`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.space[G.stripGapSpace]};
  margin: 0;
  padding: ${({ theme }) => theme.space[G.stripPaddingYSpace]} ${({ theme }) => theme.space[G.stripPaddingXSpace]};
  list-style: none;
  overflow-x: auto;
  white-space: nowrap;
  scrollbar-width: none;
  -webkit-overflow-scrolling: touch;
  &::-webkit-scrollbar {
    display: none;
  }
`;

/** Mirrors the real `StripItem` (a flex child in the mobile strip). */
export const PlaceholderItem = styled.li`
  flex: 0 0 auto;
`;

/** Mirrors the real `BarItem`: `display: flex` so the item is exactly the trigger's height, no line-box slack. */
export const PlaceholderBarItem = styled.li`
  display: flex;
  flex: 0 0 auto;
`;

export const PlaceholderTrigger = styled.span<{ $isActive?: boolean }>`
  display: inline-flex;
  align-items: center;
  height: ${pxToRem(G.triggerHeightPx)};
  padding: 0 ${({ theme }) => theme.space[G.triggerPaddingXSpace]};
  color: ${({ theme, $isActive }) => ($isActive ? theme.colors.mono14 : theme.colors.mono10)};
  font-size: ${pxToRem(G.triggerFontSizePx)};
  font-weight: 600;
  line-height: 1;
  white-space: nowrap;
`;

export const PlaceholderChip = styled.span<{ $isActive?: boolean }>`
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.space[G.chipIconGapSpace]};
  height: ${pxToRem(G.chipHeightPx)};
  padding: 0 ${pxToRem(G.chipPaddingRightPx)} 0 ${pxToRem(G.chipPaddingLeftPx)};
  border: 1px solid transparent;
  color: ${({ theme, $isActive }) => ($isActive ? theme.colors.mono14 : theme.colors.mono10)};
  font-size: ${pxToRem(G.chipFontSizePx)};
  font-weight: 600;
  line-height: 1;
  white-space: nowrap;

  /* Reserves the chevron's width so the chip is exactly as wide as the real one. */
  &::after {
    content: '';
    display: inline-block;
    width: ${pxToRem(G.chipIconPx)};
    height: ${pxToRem(G.chipIconPx)};
  }

  /* The real chip's active underline (\`StripChip\`), so the active chip doesn't change when the nav loads. */
  ${({ $isActive, theme }) =>
    $isActive &&
    css`
      &::before {
        content: '';
        position: absolute;
        left: ${pxToRem(G.chipPaddingLeftPx)};
        right: ${pxToRem(G.chipPaddingRightPx)};
        bottom: calc(-1 * ${theme.space[G.stripPaddingYSpace]} - 1px);
        height: 2px;
        background-color: ${theme.colors.mono14};
      }
    `}
`;
